/**
 * CRM Values — the configurable dropdown values an organisation keeps for
 * Contacts and Deals: Lifecycle Stage, Engagement Status and Deal Stage.
 *
 * Mirrors the `/crm/values/{category}` contract (CRM_Values_API.md).
 *
 * Note what a value is *not*: the `key` (`SALES_VERIFIED`) is what Contact and
 * Deal records carry, and it never changes, even when an admin renames the
 * value. So `key` is an identifier, never something to put on screen — `name`
 * is the only field a user should ever read.
 */

export type CrmValueCategory =
  | "LIFECYCLE_STAGE"
  | "ENGAGEMENT_STATUS"
  | "DEAL_STAGE";

export type CrmValueStatus = "ACTIVE" | "INACTIVE";

export interface CrmValue {
  uuid: string;
  /** Permanent code the Contact/Deal records carry. Never displayed. */
  key: string;
  category: CrmValueCategory;
  name: string;
  /** Omitted by the API when empty — §4 drops null fields rather than sending them. */
  description?: string;
  status: CrmValueStatus;
  /** The order the API wants the list shown in; it sorts, we do not. */
  sortOrder: number;
  /** Deal Stage only, and omitted for admin-created stages. */
  probability?: number;
  closedWon: boolean;
  closedLost: boolean;
  /** Contacts (or Deals) currently using the value. */
  usageCount: number;
}

/** Why a value cannot be deleted at all — §1's two minimum-value rules. */
export type CrmValueBlockCode = "LAST_REMAINING" | "LAST_ACTIVE";

/**
 * GET /crm/values/{category}/{valueUuid}/usage — what the Delete modal is built
 * from. A snapshot: the server recounts when the delete is confirmed.
 */
export interface CrmValueUsage {
  uuid: string;
  name: string;
  category: CrmValueCategory;
  /** "Contacts" or "Deals", for "used by 126 Contacts". */
  recordType: string;
  usageCount: number;
  /** False means do not attempt the delete — show `blockReason` instead. */
  canDelete: boolean;
  /** Omitted when `canDelete` is true (§4 drops empty fields). */
  blockCode?: CrmValueBlockCode;
  /** Already written for the user; shown as-is. */
  blockReason?: string;
  /** The Replace With options: Active values of this category, minus this one. */
  replacementOptions: CrmValue[];
}

/** DELETE /crm/values/{category}/{valueUuid} — what was done. */
export interface CrmValueDeleteResult {
  category: CrmValueCategory;
  deletedValue: string;
  /** Omitted when nothing needed moving. */
  replacementValue?: string;
  /** Counted by the server at confirm time; may differ from what the modal showed. */
  reassignedCount: number;
}

/** Body of both add and edit. */
export interface CrmValuePayload {
  name: string;
  description?: string;
}

/** The tabs, in the order the screen shows them. */
export const CRM_VALUE_CATEGORIES: CrmValueCategory[] = [
  "LIFECYCLE_STAGE",
  "ENGAGEMENT_STATUS",
  "DEAL_STAGE",
];

/** What each category is called in a path segment. */
export const CRM_VALUE_CATEGORY_PATHS: Record<CrmValueCategory, string> = {
  LIFECYCLE_STAGE: "lifecycle-stage",
  ENGAGEMENT_STATUS: "engagement-status",
  DEAL_STAGE: "deal-stage",
};

/** Tab label, modal title and the "Add …" button, all from one place. */
export const CRM_VALUE_CATEGORY_LABELS: Record<CrmValueCategory, string> = {
  LIFECYCLE_STAGE: "Lifecycle Stage",
  ENGAGEMENT_STATUS: "Engagement Status",
  DEAL_STAGE: "Deal Stage",
};

/** Which records carry the category — for "currently used by 126 Contacts". */
export const CRM_VALUE_RECORD_TYPES: Record<CrmValueCategory, string> = {
  LIFECYCLE_STAGE: "Contacts",
  ENGAGEMENT_STATUS: "Contacts",
  DEAL_STAGE: "Deals",
};

/** An example of the kind of value each set holds, for the Name field. */
export const CRM_VALUE_PLACEHOLDERS: Record<CrmValueCategory, string> = {
  LIFECYCLE_STAGE: "e.g. Prospect",
  ENGAGEMENT_STATUS: "e.g. Awaiting Reply",
  DEAL_STAGE: "e.g. Negotiation",
};

/** The API's own limit, so a save is not sent to be refused. */
export const CRM_VALUE_NAME_MAX = 100;

/**
 * The house format for a value's name: first letter upper case, the rest lower
 * — `hot PROSPECT` becomes `Hot prospect`. Surrounding spaces are dropped.
 */
export const formatCrmValueName = (name: string): string => {
  const trimmed = name.trim();
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
};

/**
 * A product limit, tighter than the API's 1000: a description is a line of
 * help under a dropdown value, not a paragraph. Values saved before this limit
 * may be longer — the edit form asks for those to be shortened before saving.
 */
export const CRM_VALUE_DESCRIPTION_MAX = 200;
